import { useCurrentUser } from '../use-current-user'
import useSWR from 'swr'
import type { Workspace, WorkspaceSubscription } from '@/lib/types'

/**
 * Simplified project info for workspace display
 */
export interface IProjectInfo {
  _id: string
  short_id: string
  project_name: string
  workspace_id?: string
  user_id?: string
  status?: 'live' | 'in progress' | 'draft'
  visibility?: 'public' | 'private'
  initial_coding_stage?: string
  create_time?: Date
  own_domain?: string
  new_domain?: string
  default_domain?: string
  thumbnail?: string
  deploy_url?: string
}

/**
 * Workspace with enriched subscription and limits data
 */
export interface IWorkspaceWithSubscription
  extends Omit<Workspace, 'subscription'> {
  subscription: WorkspaceSubscription | null
  projects: IProjectInfo[]
  project_count: number
  available_coins?: number // Available coins for the workspace
  limits: {
    plan: string
    billing_cycle: string | null
    max_domains: number
    current_domains: number
    remaining_domains: number
    can_create_more: boolean
    credits_per_month: number
  }
  // User's relationship to this workspace
  is_owner: boolean
  user_role: 'owner' | 'admin' | 'member' | 'viewer'
}

/**
 * Hook to fetch all user's workspaces with subscription and limits info
 * @returns All workspaces with enriched data
 */
export function useWorkspaces() {
  const { data: user } = useCurrentUser()

  const { data, error, mutate } = useSWR<{
    success: boolean
    data: IWorkspaceWithSubscription[]
  }>(
    user ? '/api/me/workspaces' : null,
    (url: string) => fetch(url).then((res) => res.json()),
    {
      revalidateIfStale: true,
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      dedupingInterval: 5 * 60 * 1000, // Cache for 5 minutes
    },
  )

  const workspaces = data?.data || []

  // Find workspaces owned by the user (only owners can manage subscriptions)
  const ownedWorkspaces = workspaces.filter((w) => w.is_owner)

  // Find workspaces with active subscriptions
  const workspacesWithActiveSubscriptions = workspaces.filter(
    (w) =>
      w.subscription &&
      (w.subscription.status === 'active' ||
        w.subscription.status === 'past_due'),
  )

  // Find first workspace with active subscription, or fallback to first workspace
  const defaultWorkspace = workspacesWithActiveSubscriptions[0] || workspaces[0]

  return {
    workspaces,
    ownedWorkspaces,
    workspacesWithActiveSubscriptions,
    defaultWorkspace,
    defaultWorkspaceId: defaultWorkspace?._id?.toString() || null,
    loading: !data && !error && !!user,
    error,
    mutate,
  }
}
